// router/index.ts
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";

import ActivityLogsView from "../views/admin-activity-logs.vue";
import Auth from "../services/auth";
import AuthGuard from "../services/auth-guard";
import BasestationView from "../views/admin-base-station-files.vue";
import BulkProcessing from "../views/admin-bulk-processing.vue";
import ImportedScansView from "../views/admin-imported-scans.vue";
import LayersView from "../views/admin-layers.vue";
import LogoutSuccess from "../components/logout-success.vue";
import OrganizationView from "../views/admin-organizations.vue";
import PackageUploadView from "../views/package-upload.vue";
import PipelineManagementView from "../views/admin-pdal-pipelines.vue";
import ProjectsView from "../views/admin-projects.vue";
import TrajectoryProcessingView from "../views/admin-trajectory-processing.vue";
import UsersView from "../views/admin-users.vue";
// import FullViewer from "../views/full-viewer.vue";
// import Viewer from "../views/scan-viewer.vue";

const routes: Array<RouteRecordRaw> = [
  {
    // Native in-SPA sign-in page (direct Cognito auth — no hosted UI).
    // Supports ?email= prefill and ?redirect= post-login target.
    path: "/login",
    name: "Login",
    meta: {
      fullscreen: true,
    },
    component: () => import("../components/login-page.vue"),
  },
  {
    path: "/login/oauth2/code/cognito",
    component: { render: () => null },
    beforeEnter: async (_to, _from, next) => {
      try {
        await Auth.parseCognitoWebResponse(window.location.href);
        next("/");
      } catch (error) {
        console.error("Error processing OAuth response:", error);
        next("/unauthorized");
      }
    },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    meta: {
      fullscreen: true,
    },
    component: LogoutSuccess,
    beforeEnter(_to, _from, next) {
      next();
    },
  },
  {
    // First-login "choose your password" page. Reached UNAUTHENTICATED from
    // the invitation email; the alias keeps legacy /welcome.html links working
    // (CloudFront serves the SPA for unknown paths).
    path: "/welcome",
    alias: "/welcome.html",
    name: "SetPassword",
    meta: {
      fullscreen: true,
    },
    component: () => import("../components/set-password.vue"),
  },
  {
    // Self-service reset, step 1: ask Cognito to email a one-time link.
    // Reached UNAUTHENTICATED from the "Forgot password?" link on /login.
    path: "/forgot-password",
    name: "ForgotPassword",
    meta: {
      fullscreen: true,
    },
    component: () => import("../components/forgot-password.vue"),
  },
  {
    // Self-service reset, step 2: "choose a new password". Reached
    // UNAUTHENTICATED from the emailed link, which carries the confirmation
    // code in the fragment (/reset#c=<code>).
    path: "/reset",
    alias: "/reset.html",
    name: "ResetPassword",
    meta: {
      fullscreen: true,
    },
    component: () => import("../components/reset-password.vue"),
  },
  {
    path: "/",
    name: "Viewer",
    meta: {
      menu: true,
      title: "Scans",
      icon: "mdi-map-legend",
      userTypes: ["Admin", "Power User", "Standard"],
      projectSelector: true,
    },
    component: () => import("../views/scan-viewer.vue"),
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/full-viewer",
    name: "Full Viewer",
    meta: {
      menu: true,
      title: "Scans (Full Viewer)",
      icon: "mdi-map",
      userTypes: ["Admin"],
      projectSelector: true,
    },
    component: () => import("../views/full-viewer.vue"),
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/layers",
    name: "Layers",
    meta: {
      menu: true,
      title: "Layers",
      icon: "mdi-layers-outline",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: LayersView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/pipeline-management",
    name: "PipelineManagement",
    meta: {
      menu: true,
      title: "Pipeline Management",
      icon: "mdi-pipe",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: PipelineManagementView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/imported-scans",
    name: "Imported Scans",
    meta: {
      menu: true,
      title: "Imported Scans",
      icon: "mdi-cloud-upload-outline",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: ImportedScansView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/basestation",
    name: "Basestation",
    meta: {
      menu: true,
      title: "Basestation Files",
      icon: "mdi-video-input-antenna",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: BasestationView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/trajectory-processing",
    name: "TrajectoryProcessing",
    meta: {
      menu: true,
      title: "Trajectory Processing",
      icon: "mdi-map-marker-path",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: TrajectoryProcessingView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/bulk-processing",
    name: "Bulk Processing",
    meta: {
      menu: true,
      title: "Bulk Processing",
      icon: "mdi-cloud-sync-outline",
      userTypes: ["Admin", "Power User"],
      projectSelector: true,
    },
    component: BulkProcessing,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/device-assignment",
    name: "DeviceAssignment",
    meta: {
      menu: true,
      title: "Device Assignment",
      icon: "mdi-book-clock-outline",
      userTypes: ["Admin", "Power User"],
    },
    component: () => import("../views/admin-device-assignment.vue"),
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/projects",
    name: "Projects",
    meta: {
      menu: true,
      title: "Projects",
      icon: "mdi-notebook-outline",
      userTypes: ["Admin", "Power User"],
    },
    component: ProjectsView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/organizations",
    name: "Organization",
    meta: {
      menu: true,
      title: "Organizations",
      icon: "mdi-home-city-outline",
      userTypes: ["Admin"],
    },
    component: OrganizationView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/users",
    name: "Users",
    meta: {
      menu: true,
      title: "Users",
      icon: "mdi-account-multiple",
      userTypes: ["Admin", "Power User"],
    },
    component: UsersView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/activity-logs",
    name: "ActivityLogs",
    meta: {
      menu: true,
      title: "Activity Logs",
      icon: "mdi-book-open-variant",
      userTypes: ["Admin", "Power User"],
    },
    component: ActivityLogsView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/package-upload",
    name: "PackageUpload",
    meta: {
      menu: true,
      title: "Package Upload",
      icon: "mdi-file-upload-outline",
      userTypes: [],
    },
    component: PackageUploadView,
    beforeEnter: AuthGuard.canActivate,
  },
  {
    path: "/panorama-viewer",
    name: "PanoramaViewer",
    meta: {
      fullscreen: true,
    },
    component: () => import("../views/panorama-viewer.vue"),
    beforeEnter: AuthGuard.canActivate,
  },
  {
    // Sign-out happens entirely inside the SPA (Auth.logout purges local
    // state; no external redirect), then the page forwards to /login. The
    // alias keeps the legacy static /loggedout.html target (and the hosted-UI
    // fallback's logout_uri) working through the SPA fallback.
    path: "/logout",
    alias: "/loggedout.html",
    name: "Logout",
    meta: {
      menu: true,
      title: "Logout",
      icon: "mdi-account",
      userTypes: [],
      fullscreen: true,
    },
    component: LogoutSuccess,
    beforeEnter(_to, _from, next) {
      Auth.logout();
      next();
    },
  },
];

// Optional: If you want to keep a pattern similar to your existing
// createRouter/getRouter logic:
let routerInstance: ReturnType<typeof createRouter> | null = null;

export const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(process.env.VUE_APP_BASE_URL),
    routes,
  });

  routerInstance = router;
  return router;
};

export const getRouter = () => {
  if (!routerInstance) {
    throw new Error("Router has not been created yet.");
  }
  return routerInstance;
};

// By default, export a router instance:
export default createAppRouter();
